import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Mail, Crown } from 'lucide-react';
import { Button } from './Button';
import { LimitReachedModal } from './LimitReachedModal';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { notifications } from '../lib/notifications';
interface InviteFriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
  questId: string;
  questTitle: string;
  onInvite: (emails: string[]) => Promise<void>;
  currentCollaborators?: number;
}
export function InviteFriendsModal({
  isOpen,
  onClose,
  questId,
  questTitle,
  onInvite,
  currentCollaborators = 1
}: InviteFriendsModalProps) {
  const navigate = useNavigate();
  const {
    tier,
    limits
  } = useSubscription();
  const [email, setEmail] = useState('');
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const handleAddEmail = () => {
    // Check collaborative quest limit for free users
    if (tier === 'free' && currentCollaborators >= limits.maxCollaborativeQuests) {
      setShowLimitModal(true);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && emailRegex.test(email) && !invitedEmails.includes(email)) {
      setInvitedEmails([...invitedEmails, email]);
      setEmail('');
    }
  };
  const handleRemoveEmail = (emailToRemove: string) => {
    setInvitedEmails(invitedEmails.filter(e => e !== emailToRemove));
  };
  const handleSendInvites = async () => {
    if (invitedEmails.length === 0) return;
    setLoading(true);
    try {
      await onInvite(invitedEmails);
      notifications.success('Invites Sent!', `${invitedEmails.length} invitation${invitedEmails.length !== 1 ? 's' : ''} sent successfully`);
      setInvitedEmails([]);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  return <>
      <AnimatePresence data-id="element-304">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-305">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} className="bg-white dark:bg-[#252936] rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-306">
              {/* Header */}
              <div className="flex items-center justify-between mb-6" data-id="element-307">
                <div data-id="element-308">
                  <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-309">
                    Invite Friends
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand mt-1" data-id="element-310">
                    Collaborate on:{' '}
                    <span className="font-bold" data-id="element-311">{questTitle}</span>
                  </p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-312">
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-313" />
                </button>
              </div>

              {/* Free Tier Warning */}
              {tier === 'free' && <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl" data-id="element-314">
                  <div className="flex items-start gap-3" data-id="element-315">
                    <Crown className="w-5 h-5 text-game-purple flex-shrink-0 mt-0.5" data-id="element-316" />
                    <div data-id="element-317">
                      <p className="text-sm font-fredoka font-bold text-purple-900 dark:text-purple-100 mb-1" data-id="element-318">
                        Free Tier Limit
                      </p>
                      <p className="text-xs text-purple-800 dark:text-purple-200 font-quicksand" data-id="element-319">
                        Free users can have 1 collaborative quest. Upgrade to
                        Premium for unlimited collaboration!
                      </p>
                    </div>
                  </div>
                </div>}

              {/* Email Input */}
              <div className="mb-6" data-id="element-320">
                <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-2" data-id="element-321">
                  Enter email addresses
                </label>
                <div className="flex gap-2" data-id="element-322">
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddEmail();
                }
              }} placeholder="friend@example.com" className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936] text-game-dark dark:text-white font-quicksand focus:outline-none focus:border-game-purple" data-id="element-323" />
                  <Button onClick={handleAddEmail} icon={<UserPlus className="w-5 h-5" data-id="element-325" />} data-id="element-324">
                    Add
                  </Button>
                </div>
              </div>

              {/* Invited Emails List */}
              {invitedEmails.length > 0 && <div className="mb-6" data-id="element-326">
                  <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-327">
                    Invited ({invitedEmails.length})
                  </label>
                  <div className="space-y-2" data-id="element-328">
                    {invitedEmails.map(invitedEmail => <motion.div key={invitedEmail} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl" data-id="element-329">
                        <div className="flex items-center gap-3" data-id="element-330">
                          <div className="w-10 h-10 bg-game-purple/20 rounded-full flex items-center justify-center" data-id="element-331">
                            <Mail className="w-5 h-5 text-game-purple" data-id="element-332" />
                          </div>
                          <span className="font-quicksand text-game-dark dark:text-white" data-id="element-333">
                            {invitedEmail}
                          </span>
                        </div>
                        <button onClick={() => handleRemoveEmail(invitedEmail)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-334">
                          <X className="w-4 h-4 text-red-600 dark:text-red-400" data-id="element-335" />
                        </button>
                      </motion.div>)}
                  </div>
                </div>}

              {/* Info Box */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl" data-id="element-336">
                <p className="text-sm text-blue-800 dark:text-blue-200 font-quicksand" data-id="element-337">
                  <strong data-id="element-338">How it works:</strong> Your friends will receive an
                  email invitation and an in-app notification. Once they accept,
                  they can view and complete this quest together with you!
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3" data-id="element-339">
                <Button variant="secondary" onClick={onClose} className="flex-1" data-id="element-340">
                  Cancel
                </Button>
                <Button onClick={handleSendInvites} disabled={invitedEmails.length === 0 || loading} className="flex-1" icon={<Mail className="w-5 h-5" data-id="element-342" />} data-id="element-341">
                  {loading ? 'Sending...' : `Send ${invitedEmails.length} Invite${invitedEmails.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <LimitReachedModal isOpen={showLimitModal} onClose={() => setShowLimitModal(false)} onUpgrade={() => {
      setShowLimitModal(false);
      onClose();
      navigate('/upgrade');
    }} limitType="collaborative" data-id="element-343" />
    </>;
}